import { useState, useRef } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { EmptyState } from '@/components/ui/EmptyState'
import { Spinner } from '@/components/ui/Spinner'
import { formatDate } from '@/lib/utils'
import {
  FileIcon,
  Plus,
  Download,
  Trash2,
  Upload,
  Link2,
  Copy,
  Check,
  FileImage,
  FileText,
  File,
  FileSpreadsheet,
  Clock,
  Share2,
  X,
} from 'lucide-react'
import type { ProjectDocument, DocumentCategory } from '@/types/project'
import { DOCUMENT_CATEGORY_CONFIG } from '@/types/project'
import {
  useProjectDocuments,
  useUploadDocument,
  useDeleteDocument,
  useGenerateShareLink,
} from '@/hooks/useProjects'
import { useAuth } from '@/contexts/AuthContext'

interface DocumentsTabProps {
  projectId: string
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function getFileIcon(type: string) {
  if (type.includes('pdf')) return FileText
  if (type.includes('image')) return FileImage
  if (type.includes('word') || type.includes('document')) return FileText
  if (type.includes('excel') || type.includes('spreadsheet')) return FileSpreadsheet
  return File
}

function CategoryBadge({ category }: { category: DocumentCategory }) {
  const config = DOCUMENT_CATEGORY_CONFIG[category]
  return <Badge variant={config.color}>{config.label}</Badge>
}

function UploadModal({
  isOpen,
  onClose,
  projectId,
}: {
  isOpen: boolean
  onClose: () => void
  projectId: string
}) {
  const { user } = useAuth()
  const [file, setFile] = useState<File | null>(null)
  const [category, setCategory] = useState<DocumentCategory>('autre')
  const [description, setDescription] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const uploadMutation = useUploadDocument()

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0])
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) return

    await uploadMutation.mutateAsync({
      projectId,
      file,
      categorie: category,
      description: description.trim() || undefined,
      uploadedBy: user?.id,
    })

    setFile(null)
    setCategory('autre')
    setDescription('')
    onClose()
  }

  const handleReset = () => {
    setFile(null)
    setCategory('autre')
    setDescription('')
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleReset} title="Ajouter un document">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Drop zone */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-mint-500 bg-mint-50'
              : file
              ? 'border-mint-400 bg-mint-50'
              : 'border-beige-300 hover:border-beige-400'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />

          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileIcon className="w-8 h-8 text-mint-600" />
              <div className="text-left">
                <p className="font-medium text-brown-900">{file.name}</p>
                <p className="text-sm text-brown-500">{formatFileSize(file.size)}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setFile(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 text-beige-400 mx-auto mb-3" />
              <p className="text-brown-600 mb-1">
                Glissez un fichier ici ou{' '}
                <button
                  type="button"
                  className="text-mint-600 font-medium hover:underline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  parcourez
                </button>
              </p>
              <p className="text-sm text-brown-400">
                PDF, Images, Word, Excel (max 10 MB)
              </p>
            </>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">
            Categorie
          </label>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value as DocumentCategory)}
          >
            {Object.entries(DOCUMENT_CATEGORY_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label}
              </option>
            ))}
          </Select>
        </div>

        {/* Description */}
        <Input
          label="Description (optionnel)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Ajoutez une description..."
        />

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={handleReset}>
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={!file || uploadMutation.isPending}
            leftIcon={
              uploadMutation.isPending ? (
                <Spinner size="sm" />
              ) : (
                <Upload className="w-4 h-4" />
              )
            }
          >
            {uploadMutation.isPending ? 'Upload...' : 'Telecharger'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function ShareLinkModal({
  isOpen,
  onClose,
  documentId,
}: {
  isOpen: boolean
  onClose: () => void
  documentId: string
}) {
  const [link, setLink] = useState('')
  const [copied, setCopied] = useState(false)
  const [expiresIn, setExpiresIn] = useState(24)
  const generateLink = useGenerateShareLink()

  const handleGenerate = async () => {
    const shareLink = await generateLink.mutateAsync({
      documentId,
      expiresInHours: expiresIn,
    })
    setLink(shareLink)
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleClose = () => {
    setLink('')
    setCopied(false)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Generer un lien de partage">
      <div className="space-y-4">
        <p className="text-sm text-brown-600">
          Generez un lien temporaire pour partager ce document avec des personnes
          externes.
        </p>

        {!link ? (
          <>
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-1">
                Expiration du lien
              </label>
              <Select
                value={expiresIn.toString()}
                onChange={(e) => setExpiresIn(parseInt(e.target.value))}
              >
                <option value="1">1 heure</option>
                <option value="6">6 heures</option>
                <option value="24">24 heures</option>
                <option value="72">3 jours</option>
                <option value="168">7 jours</option>
              </Select>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleClose}>
                Annuler
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={generateLink.isPending}
                leftIcon={
                  generateLink.isPending ? (
                    <Spinner size="sm" />
                  ) : (
                    <Link2 className="w-4 h-4" />
                  )
                }
              >
                Generer le lien
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-beige-50 rounded-lg border border-beige-200">
              <Input
                value={link}
                readOnly
                className="flex-1 bg-transparent border-none"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                leftIcon={
                  copied ? (
                    <Check className="w-4 h-4 text-mint-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )
                }
              >
                {copied ? 'Copie !' : 'Copier'}
              </Button>
            </div>
            <div className="flex items-center gap-2 text-sm text-brown-500">
              <Clock className="w-4 h-4" />
              <span>Ce lien expirera dans {expiresIn} heures</span>
            </div>
            <Button variant="outline" className="w-full" onClick={handleClose}>
              Fermer
            </Button>
          </div>
        )}
      </div>
    </Modal>
  )
}

function DocumentRow({
  document,
  projectId,
}: {
  document: ProjectDocument
  projectId: string
}) {
  const [showShareModal, setShowShareModal] = useState(false)
  const deleteMutation = useDeleteDocument()
  const FileIconComponent = getFileIcon(document.type)

  const handleDelete = async () => {
    if (confirm('Voulez-vous vraiment supprimer ce document ?')) {
      await deleteMutation.mutateAsync({ documentId: document.id, projectId })
    }
  }

  return (
    <>
      <tr className="hover:bg-beige-50 transition-colors">
        <td className="px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-beige-100 flex items-center justify-center">
              <FileIconComponent className="w-5 h-5 text-brown-600" />
            </div>
            <div>
              <p className="font-medium text-brown-900">{document.nom}</p>
              {document.description && (
                <p className="text-sm text-brown-500 line-clamp-1">
                  {document.description}
                </p>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-4">
          <CategoryBadge category={document.categorie} />
        </td>
        <td className="px-4 py-4 text-sm text-brown-600">
          {formatFileSize(document.taille)}
        </td>
        <td className="px-4 py-4 text-sm text-brown-600">
          {document.uploader?.full_name || '-'}
        </td>
        <td className="px-4 py-4 text-sm text-brown-500">
          {formatDate(document.created_at, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </td>
        <td className="px-4 py-4 text-right">
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowShareModal(true)}
              title="Generer un lien de partage"
            >
              <Share2 className="w-4 h-4" />
            </Button>
            <a href={document.url} target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="sm" title="Telecharger">
                <Download className="w-4 h-4" />
              </Button>
            </a>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              title="Supprimer"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        </td>
      </tr>

      <ShareLinkModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        documentId={document.id}
      />
    </>
  )
}

export function DocumentsTab({ projectId }: DocumentsTabProps) {
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | ''>('')

  const { data: documents, isLoading } = useProjectDocuments(projectId)

  const filteredDocuments = categoryFilter
    ? documents?.filter((d) => d.categorie === categoryFilter)
    : documents

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as DocumentCategory | '')}
            className="w-40"
          >
            <option value="">Toutes categories</option>
            {Object.entries(DOCUMENT_CATEGORY_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label}
              </option>
            ))}
          </Select>
          <span className="text-sm text-brown-500">
            {filteredDocuments?.length || 0} document
            {(filteredDocuments?.length || 0) > 1 ? 's' : ''}
          </span>
        </div>
        <Button
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setShowUploadModal(true)}
        >
          Ajouter un document
        </Button>
      </div>

      {/* Documents list */}
      {!filteredDocuments || filteredDocuments.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={<FileIcon className="w-12 h-12" />}
            title="Aucun document"
            description={
              categoryFilter
                ? 'Aucun document dans cette categorie'
                : 'Ajoutez des documents a ce projet'
            }
            action={
              !categoryFilter && (
                <Button
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => setShowUploadModal(true)}
                >
                  Ajouter un document
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-beige-50 border-b border-beige-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Document
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Categorie
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Taille
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Ajoute par
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-beige-200">
                {filteredDocuments.map((doc) => (
                  <DocumentRow key={doc.id} document={doc} projectId={projectId} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Upload modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        projectId={projectId}
      />
    </div>
  )
}

export default DocumentsTab
